import { useDispatch, useSelector } from 'react-redux'
import {
    DashboardSyncEvents,
    DashboardSyncSettings,
    IWidget,
    WidgetProps,
    WidgetSettings,
} from '../../widgetDashboard.interface'
import {
    forwardRef,
    memo,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import { getIsWidgetSlotActiveSelector } from '../../../../selectors/widgetDashboard'
import { deferAfterPaneSelectionPaint } from '../../deferPaneSideEffects'
import store from '../../../../store'
import useSetPrimarySecurity from '../../../../hooks/useSetPrimarySecurity'
import { deleteChartConfig, setActiveChart, updateChartConfig } from '../../../../reducer/chart.reducer'
import { updateWidgetSettings, updateWidgetType } from '../../../../reducer/widgetDashboard.reducer'
import { AppContext } from '../../../../App'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { fetchOptionExpiriesV2 } from '../../../../util/options'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import { Segment } from '../../../../proto/generated/security'
import { Box, Button, Stack, Typography } from '@mui/material'
import SettingsOutlinedIcon from '../../../../assets/icons/SettingOutlined'
import { NonInteractiveTooltip } from '../../../custom'
import { WidgetTypes } from '../../widgetDashboard.enums'
import { selectActiveLayoutId } from '../../../../selectors/home'
import { isExpressionMode } from '../../../../util/expression'
import {
    selectChartConfig,
    selectExchangeTradingSessions,
    selectOpenInterestChartSettings,
    selectTradingCalendarDataByExchange,
} from '../../../../selectors/chart'
import { getDefaultChartConfig } from '../../../../util/chart'
import { OpenInterestChartSettings } from '../../../chartV2/chart.typings'
import { Exchange } from '../../../../proto/generated/common'
import dayjs from 'dayjs'
import { getEffectiveTradingDate, getPreviousValidTradingDay } from '../../../../util/tradingCalendar'
import { getBeforeRegularSessionOpenInfo } from '../../../../util/tradingSessionLive'
import { resolveLiveTradingSessionsForSecurity } from '../../../../util/tradingSessionSelection'
import OiAnalysisChartView from '../../../../pages/OpenInterestAnalysis/OiAnalysisChartView'
import { OI_WIDGET_BOTTOM_SCALE_WIDTH, OI_WIDGET_RIGHT_SCALE_WIDTH } from '../../../../pages/OpenInterestAnalysis/OIChart'
import { useOiAnalysisChartData } from '../../../../pages/OpenInterestAnalysis/useOiAnalysisChartData'
import ChartSettingsButtons from '../../../chartV2/chartContextMenuButton/ChartSettingsButtons'
import OpenInterestSettingsDialog from './OpenInterestSettingsDialog'
import OpenInterestWidgetMetadata from './OpenInterestWidgetMetadata'
import OiChartSkeletonOverlay from '../../../common/OiChartSkeletonOverlay'
import type { OIAnalysisSettings } from '../../../../reducer/multiStrikeChart.reducer'
import type { OiChartRow } from '../../../../pages/OpenInterestAnalysis/oiChartData'

export type OpenInterestChartWidgetSettings = WidgetSettings<{
    chartId: string
}>

const OpenInterestChartWidget = forwardRef<any, WidgetProps<OpenInterestChartWidgetSettings>>(
    ({ dashboardId, slotId, settings, allowSplit }, ref) => {
        const dispatch = useDispatch()
        const chartId = settings.chartId ?? `${dashboardId}-${slotId}`
        const appContext = useContext(AppContext)
        const setPrimarySecurity = useSetPrimarySecurity()
        const mounted = useRef(false)
        const removed = useRef(false)
        const { key, expressionMode, securities } = settings
        const [securityLoading, setSecurityLoading] = useState(true)
        const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
        const [settingsOpen, setSettingsOpen] = useState(false)
        const [hoverRow, setHoverRow] = useState<OiChartRow | null>(null)
        const [sessionBoundsKey, setSessionBoundsKey] = useState(0)
        const [autoManagedPremarketDate, setAutoManagedPremarketDate] = useState(false)
        const userPickedDateRef = useRef(false)
        const isActiveSlot = useSelector(getIsWidgetSlotActiveSelector(dashboardId, slotId))
        const previousSecurityInfo = useRef({ key, expressionMode, securities })
        const activeLayoutId = useSelector(selectActiveLayoutId)
        const chartConfig = useSelector(selectChartConfig(chartId))
        const oiSettings = useSelector(selectOpenInterestChartSettings(chartId))
        const oiSettingsRef = useRef(oiSettings)
        oiSettingsRef.current = oiSettings
        const calendarEvents = useSelector(
            selectTradingCalendarDataByExchange(security?.exchange ?? Exchange.NSE)
        )
        const exchangeTradingSessions = useSelector(selectExchangeTradingSessions)

        useEffect(() => {
            if (!key) return
            let cancelled = false
            setSecurityLoading(true)
            if (isExpressionMode(key)) {
                setSecurityLoading(false)
                return
            }
            getSecurityAsync(key).then(async (newSecurity) => {
                if (cancelled) return
                if (!newSecurity) {
                    setSecurity(undefined)
                    setSecurityLoading(false)
                    return
                }
                if ([Segment.FO, Segment.CD].includes(newSecurity.segment)) {
                    const underlying = await getSecurityAsync(
                        getSecurityKey(newSecurity.exchange, newSecurity.underlyingSecurityCode ?? '')
                    )
                    if (!cancelled) setSecurity(underlying ?? newSecurity)
                } else {
                    if (!cancelled) setSecurity(newSecurity)
                }
                if (!cancelled) setSecurityLoading(false)
            })
            return () => {
                cancelled = true
            }
        }, [key])

        const underlyingToken = useMemo(
            () => getSecurityKey(security?.exchange ?? 0, security?.code ?? ''),
            [security]
        )
        const dropdownExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(underlyingToken, true))

        useEffect(() => {
            if (!chartConfig) {
                dispatch(
                    updateChartConfig({
                        viewKey: chartId,
                        config: getDefaultChartConfig(),
                    })
                )
            }
        }, [chartConfig, chartId, dispatch])

        const handleSecuritySelected = useCallback(
            (nextKey: string, nextExpressionMode: boolean, nextSecurities: string[]) => {
                dispatch(
                    updateWidgetSettings({
                        id: dashboardId,
                        slotId,
                        settings: {
                            key: nextKey,
                            expressionMode: nextExpressionMode,
                            securities: nextSecurities,
                        },
                    })
                )
            },
            [dashboardId, dispatch, slotId]
        )

        const onSettingsPatch = useCallback(
            (patch: Partial<OpenInterestChartSettings | OIAnalysisSettings>) => {
                dispatch(
                    updateChartConfig({
                        viewKey: chartId,
                        config: {
                            openInterestChartSettings: {
                                ...oiSettingsRef.current,
                                ...patch,
                            },
                        },
                    })
                )
            },
            [chartId, dispatch]
        )

        useEffect(() => {
            if (!security?.hasFo || !underlyingToken) return
            if ((oiSettings.expiriesMenuFromApi?.length ?? 0) > 0) return

            let cancelled = false
            const applyExpiries = (
                expiries: { derived?: boolean; expiry?: { expiry?: number | string } }[]
            ) => {
                const ids = expiries
                    .filter((item) => !item.derived && item.expiry?.expiry != null)
                    .map((item) => String(item.expiry?.expiry))
                    .sort((a, b) => Number(a) - Number(b))
                if (!ids.length || cancelled) return
                onSettingsPatch({
                    expiriesMenuFromApi: ids,
                    selectedExpiries: [ids[0]],
                    expiriesMenuLoading: false,
                    securityKey: underlyingToken,
                })
            }

            if (dropdownExpiries?.length) {
                applyExpiries(dropdownExpiries)
                return () => {
                    cancelled = true
                }
            }

            fetchOptionExpiriesV2(security, dispatch).then((expiries) => {
                applyExpiries(expiries)
            })
            return () => {
                cancelled = true
            }
        }, [
            dispatch,
            dropdownExpiries,
            oiSettings.expiriesMenuFromApi,
            onSettingsPatch,
            security,
            underlyingToken,
        ])

        const onUserSettingsPatch = useCallback(
            (patch: Partial<OpenInterestChartSettings | OIAnalysisSettings>) => {
                if (patch.selectedDate != null) {
                    userPickedDateRef.current = true
                }
                onSettingsPatch(patch)
            },
            [onSettingsPatch]
        )

        useEffect(() => {
            if (!exchangeTradingSessions?.length || security?.exchange == null) return
            const sessions = resolveLiveTradingSessionsForSecurity(
                exchangeTradingSessions,
                calendarEvents,
                security
            )
            if (!sessions.length) return
            const timeoutIds: ReturnType<typeof setTimeout>[] = []
            const now = Date.now()
            for (const ts of sessions) {
                if (now < ts.startTime) {
                    timeoutIds.push(
                        setTimeout(() => setSessionBoundsKey((k) => k + 1), ts.startTime - now + 250)
                    )
                }
            }
            return () => timeoutIds.forEach(clearTimeout)
        }, [
            exchangeTradingSessions,
            calendarEvents,
            security?.exchange,
            security?.segment,
            security?.partOfCAS,
        ])

        const { beforeRegularOpen } = useMemo(
            () =>
                getBeforeRegularSessionOpenInfo(
                    exchangeTradingSessions,
                    security,
                    Date.now(),
                    calendarEvents
                ),
            [
                exchangeTradingSessions,
                calendarEvents,
                security?.exchange,
                security?.segment,
                security?.partOfCAS,
                sessionBoundsKey,
            ]
        )

        const effectiveTradingDate = useMemo(
            () => getEffectiveTradingDate(calendarEvents),
            [calendarEvents]
        )

        // Same as the standalone OI chart: today once the market is open, otherwise last trading day.
        // Layout-persisted selectedDate can be days old, so snap it on first load; after that a
        // user-picked date is kept, except for the live premarket → open roll-forward.
        useEffect(() => {
            if (!calendarEvents || !effectiveTradingDate) return

            const targetDate = beforeRegularOpen
                ? getPreviousValidTradingDay(new Date(effectiveTradingDate), calendarEvents)
                : effectiveTradingDate
            if (!targetDate) return

            const onTarget =
                oiSettings.selectedDate != null &&
                dayjs(oiSettings.selectedDate).isSame(dayjs(targetDate), 'day')

            if (onTarget) {
                if (beforeRegularOpen) {
                    setAutoManagedPremarketDate(true)
                } else if (autoManagedPremarketDate) {
                    setAutoManagedPremarketDate(false)
                }
                return
            }

            const shouldApply =
                !userPickedDateRef.current || beforeRegularOpen || autoManagedPremarketDate
            if (!shouldApply) return

            onSettingsPatch({
                selectedDate: dayjs(targetDate).startOf('day').valueOf(),
            })
            setAutoManagedPremarketDate(beforeRegularOpen)
        }, [
            beforeRegularOpen,
            autoManagedPremarketDate,
            calendarEvents,
            effectiveTradingDate,
            oiSettings.selectedDate,
            onSettingsPatch,
        ])

        const chartState = useOiAnalysisChartData({
            security,
            settings: oiSettings,
            onSettingsPatch,
            isOIChangeScreen: false,
            snapshotNamespace: `widget:${chartId}`,
            selectNearestExpiryOnLoad: true,
        })

        useEffect(() => {
            const mount = !!security

            if (isActiveSlot && mount && !mounted.current) {
                mounted.current = true
                deferAfterPaneSelectionPaint(() => {
                    dispatch(
                        setActiveChart({
                            chartId,
                            chartFocused: true,
                        })
                    )
                    const { key: currentKey, expressionMode: currentExpressionMode } =
                        store.getState().homeStore.primarySecurity
                    if (currentKey !== underlyingToken || currentExpressionMode !== expressionMode) {
                        setPrimarySecurity(
                            underlyingToken,
                            [security],
                            expressionMode,
                            security?.displayName ?? security?.symbol
                        )
                    }
                })
            }
        }, [
            underlyingToken,
            security,
            isActiveSlot,
            slotId,
            expressionMode,
            chartId,
            dispatch,
            setPrimarySecurity,
        ])

        useImperativeHandle<any, IWidget>(
            ref,
            () => {
                return {
                    onBlur() {},
                    openSettings() {
                        setSettingsOpen(true)
                    },
                    onActive() {
                        if (!security) return
                        deferAfterPaneSelectionPaint(() => {
                            dispatch(
                                setActiveChart({
                                    chartId,
                                    chartFocused: true,
                                })
                            )
                            const { key: currentKey, expressionMode: currentExpressionMode } =
                                store.getState().homeStore.primarySecurity
                            if (
                                currentKey !== underlyingToken ||
                                currentExpressionMode !== expressionMode
                            ) {
                                setPrimarySecurity(
                                    underlyingToken,
                                    [security],
                                    expressionMode,
                                    security?.displayName ?? security?.symbol
                                )
                            }
                        })
                    },
                    updateSettings(nextSettings: WidgetSettings<any>) {
                        dispatch(
                            updateWidgetSettings({
                                id: dashboardId,
                                slotId,
                                settings: nextSettings,
                            })
                        )
                    },
                    onSyncSettingsChanged(settingKey: keyof DashboardSyncSettings, value: boolean) {
                        switch (settingKey) {
                            case 'security':
                                value &&
                                    underlyingToken &&
                                    appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                                        key: underlyingToken,
                                        expressionMode,
                                        securities: [underlyingToken],
                                    })
                                break
                        }
                    },
                    onRemove() {
                        removed.current = true
                    },
                    onSecurityUpdate: (data) => {
                        dispatch(
                            updateWidgetSettings({
                                id: dashboardId,
                                slotId,
                                settings: {
                                    ...data,
                                },
                            })
                        )
                    },
                    onTimeBucketUpdate: () => {},
                    onCrosshairUpdate: () => {},
                }
            },
            [chartId, underlyingToken, security, expressionMode, slotId, dashboardId, dispatch, appContext]
        )

        useEffect(() => {
            if (!isActiveSlot) return
            if (key && key !== previousSecurityInfo.current.key) {
                appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                    key,
                    expressionMode,
                    securities,
                })
                previousSecurityInfo.current = {
                    key,
                    expressionMode,
                    securities,
                }
            }
        }, [slotId, key, expressionMode, securities, isActiveSlot, appContext])

        useEffect(() => {
            return () => {
                if (removed.current) {
                    dispatch(deleteChartConfig(chartId))
                }
            }
        }, [chartId, dispatch])

        if (!chartId) return null

        if (securityLoading) {
            return (
                <Box
                    sx={{
                        position: 'relative',
                        height: '100%',
                        width: '100%',
                        bgcolor: 'background.paper',
                    }}
                >
                    <OiChartSkeletonOverlay />
                </Box>
            )
        }

        if (!security?.hasFo) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        background: (theme) => theme.palette.background.paper,
                    }}
                >
                    <Stack gap={2}>
                        <Typography>F&O not present for this security</Typography>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                dispatch(
                                    updateWidgetType({
                                        id: activeLayoutId,
                                        slotId: Number(slotId),
                                        type: WidgetTypes.CHART,
                                    })
                                )
                            }}
                        >
                            Go to candle chart
                        </Button>
                    </Stack>
                </Box>
            )
        }

        return (
            <Box
                id={`chart-${chartId}`}
                className="chart-selector"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    minHeight: 0,
                    position: 'relative',
                    background: (theme) => theme.palette.background.paper,
                    '&:hover .float-chart-settings-button': {
                        opacity: '1 !important',
                    },
                }}
            >
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                    {security && (
                        <OpenInterestWidgetMetadata
                            security={security}
                            settings={oiSettings}
                            hoverRow={hoverRow}
                            chartData={chartState.chartData}
                            onSettingsPatch={onSettingsPatch}
                            onSecuritySelected={handleSecuritySelected}
                        />
                    )}
                    <OiAnalysisChartView
                        isOIChangeScreen={false}
                        chartData={chartState.chartData}
                        showOI={chartState.showOI}
                        replayKey={chartState.replayKey}
                        loading={chartState.loading}
                        minuteMap={chartState.minuteMap}
                        value={chartState.value}
                        onSliderChange={chartState.onSliderChange}
                        onSliderCommit={chartState.onSliderCommit}
                        onQuickSelect={chartState.onQuickSelect}
                        showTimeSlider={false}
                        footerDateLabel={chartState.footerDateLabel}
                        totalCallOi={chartState.totalCallOi}
                        totalPutOi={chartState.totalPutOi}
                        callOiChange={chartState.callOiChange}
                        putOiChange={chartState.putOiChange}
                        compact
                        showFooter={false}
                        isWidget
                        onHoverRow={setHoverRow}
                    />
                    <NonInteractiveTooltip title="Settings" placement="left-start">
                        <Button
                            variant="text"
                            color="inherit"
                            disableRipple
                            onClick={() => setSettingsOpen(true)}
                            sx={{
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bottom: 0,
                                right: 0,
                                left: 'auto',
                                minWidth: OI_WIDGET_RIGHT_SCALE_WIDTH,
                                width: OI_WIDGET_RIGHT_SCALE_WIDTH,
                                height: OI_WIDGET_BOTTOM_SCALE_WIDTH + 4,
                                minHeight: OI_WIDGET_BOTTOM_SCALE_WIDTH + 4,
                                maxHeight: OI_WIDGET_BOTTOM_SCALE_WIDTH + 4,
                                p: 0,
                                borderRadius: 0,
                                zIndex: 5,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <SettingsOutlinedIcon sx={{ width: 20, height: 20 }} />
                        </Button>
                    </NonInteractiveTooltip>
                </Box>
                {security && (
                    <ChartSettingsButtons allowSplit={allowSplit} viewKey={chartId} security={security} />
                )}
                <OpenInterestSettingsDialog
                    open={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    settings={oiSettings}
                    onSettingsPatch={onUserSettingsPatch}
                    security={security}
                />
            </Box>
        )
    }
)

OpenInterestChartWidget.displayName = 'OpenInterestChartWidget'

export default memo(OpenInterestChartWidget)
